/**
 * <s-size-guard> — Makes shoppers actively choose a size before adding to cart.
 *
 * Rendered inside the product-form partial, so it reconnects every time
 * s-variant-picker swaps that partial. Until the shopper picks a value for the
 * guarded option (default "Size"), it clears the preselected size, hides the
 * selected value in that option's legend, and locks the Add to cart button
 * with a "Select a size" label.
 *
 * Element dataset:
 *   data-product-id   — Product the choice is remembered for during this page visit.
 *   data-option-name  — Option input name to guard. Default: "Size".
 *   data-prompt       — Button label shown while no size is chosen.
 *
 * A page opened with ?variant= (a shared or deep link to one exact variant)
 * counts as already chosen.
 */
const OPENED_WITH_VARIANT = new URL(location.href).searchParams.has('variant');
const chosenProducts = new Set();

class SizeGuard extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, capture: true };
    const form = this.closest('form');
    if (!form) return;

    const productId = this.dataset.productId || '';
    const optionName = this.dataset.optionName || 'Size';
    const radios = [...form.querySelectorAll('input[type="radio"]')]
      .filter((input) => input.name === optionName);

    // Nothing to choose when the option has a single value (for example "One size").
    if (radios.length <= 1) return;

    // Remember the choice before s-variant-picker re-renders the form for it.
    form.addEventListener('change', (event) => {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.name === optionName) {
        chosenProducts.add(productId);
      }
    }, opts);

    if (OPENED_WITH_VARIANT || chosenProducts.has(productId)) return;

    radios.forEach((radio) => { radio.checked = false; });
    const legendValue = radios[0].closest('fieldset')?.querySelector('[data-selected-value]');
    if (legendValue) legendValue.hidden = true;

    const addButton = form.querySelector('button[name="add"]');
    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = true;
      addButton.setAttribute('aria-disabled', 'true');
      if (this.dataset.prompt) addButton.textContent = this.dataset.prompt;
    }

    // Safety net for keyboard submission: never add the preselected size.
    form.addEventListener('submit', (event) => {
      if (chosenProducts.has(productId)) return;
      event.preventDefault();
      event.stopPropagation();
      radios[0]?.focus();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-size-guard')) customElements.define('s-size-guard', SizeGuard);
